import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import TableRowsIcon from '@mui/icons-material/TableRows';
import type { RejectedRelationship, SkippedTable } from '../services/api';

interface MappingDiagnosticsProps {
  open: boolean;
  onClose: () => void;
  skippedTables?: SkippedTable[];
  rejectedRelationships?: RejectedRelationship[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`diagnostics-tabpanel-${index}`}
      aria-labelledby={`diagnostics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

export const MappingDiagnostics: React.FC<MappingDiagnosticsProps> = ({
  open,
  onClose,
  skippedTables = [],
  rejectedRelationships = [],
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const hasSkipped = skippedTables.length > 0;
  const hasRejected = rejectedRelationships.length > 0;

  // If we open and the current tab has no data, switch to one that does
  React.useEffect(() => {
    if (open) {
      if (tabValue === 0 && !hasSkipped && hasRejected) {
        setTabValue(1);
      } else if (tabValue === 1 && !hasRejected && hasSkipped) {
        setTabValue(0);
      }
    }
  }, [open, hasSkipped, hasRejected, tabValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Mapping Diagnostics</Typography>
        </Stack>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="diagnostics tabs">
          <Tab 
            icon={<TableRowsIcon />} 
            iconPosition="start" 
            label={`Skipped Tables (${skippedTables.length})`} 
            disabled={!hasSkipped}
          />
          <Tab 
            icon={<LinkOffIcon />} 
            iconPosition="start" 
            label={`Rejected Links (${rejectedRelationships.length})`} 
            disabled={!hasRejected}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, minHeight: 300 }}>
        <TabPanel value={tabValue} index={0}>
          {hasSkipped ? (
            <Stack spacing={2}>
              <Alert severity="warning">
                These target tables were excluded from the graph because they are missing required fields or violate grain constraints.
              </Alert>
              <List>
                {skippedTables.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.target_table}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.reason}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {idx < skippedTables.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Stack>
          ) : (
            <Box p={3} textAlign="center">
              <Typography color="text.secondary">No skipped tables.</Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {hasRejected ? (
            <Stack spacing={2}>
               <Alert severity="info">
                These potential relationships were rejected because their evidence score was below the threshold.
              </Alert>
              <List>
                {rejectedRelationships.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                             <Typography variant="subtitle2" fontFamily="monospace">
                              {item.from} ↔ {item.to}
                            </Typography>
                            <Chip 
                              label={`Score: ${item.score.toFixed(2)}`} 
                              size="small" 
                              color={item.score > 0.4 ? 'warning' : 'default'} 
                              variant="outlined" 
                            />
                          </Stack>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.reason}
                          </Typography>
                        }
                      />
                    </ListItem>
                     {idx < rejectedRelationships.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Stack>
          ) : (
             <Box p={3} textAlign="center">
              <Typography color="text.secondary">No rejected relationships.</Typography>
            </Box>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
